import type { Coding } from '@medplum/fhirtypes'
import type { Condition } from '#src/observation-logic/conditionSchema.ts'
import {
  calculationMethod,
  type DerivationMethod,
  type DerivationRule,
} from '#src/observation-logic/derivations/rules.ts'
import {
  type ConditionEnv,
  evaluateCondition,
} from '#src/observation-logic/evaluate.ts'

// How many items a total may be missing. `prorate` scales the total up to
// every scored item, rounded.
export type CompletenessPolicy =
  | { requireAll: true }
  | { allowMissing: number; prorate?: boolean }

export const SUM_SCORES_METHOD = calculationMethod(
  'sum-scores',
  'Sum of item scores',
  { formula: 'sum of item scores' }
)

export interface SumScoresRuleOptions {
  id: string
  target: Coding
  items: Coding[]
  policy: CompletenessPolicy
  // A hidden item (a branch not taken) counts as zero, or as missing.
  hiddenCounts?: 'zero' | 'missing'
  method?: DerivationMethod
}

// A total of the items' answer scores, as `{score}`.
export function sumScoresRule({
  id,
  target,
  items,
  policy,
  hiddenCounts = 'zero',
  method = SUM_SCORES_METHOD,
}: SumScoresRuleOptions): DerivationRule {
  return {
    id,
    target,
    inputs: items,
    method,
    compute: (inputs) => {
      let total = 0
      let answered = 0
      let missing = 0
      let counted = 0
      for (const item of items) {
        if (inputs.hidden(item) && hiddenCounts === 'zero') {
          continue
        }
        counted++
        const score = inputs.score(item)
        if (score === null) {
          missing++
        } else {
          total += score
          answered++
        }
      }
      const allowed = 'requireAll' in policy ? 0 : policy.allowMissing
      if (missing > allowed || (counted > 0 && answered === 0)) {
        return null
      }
      const prorated =
        'prorate' in policy && policy.prorate && missing > 0
          ? Math.round((total * counted) / answered)
          : total
      return { kind: 'number', value: prorated, unit: '{score}' }
    },
  }
}

// One interpretation band of a total. `when` narrows it, such as a SLUMS
// band that depends on years of education.
export interface ScoreBand {
  min?: number
  max?: number
  code: Coding
  display: string
  severity: 'green' | 'yellow' | 'orange' | 'red'
  when?: Condition
}

// The first band holding the value whose condition, if any, holds. A
// condition that cannot be decided does not hold. Bounds are inclusive; an
// absent bound is open.
export function interpretScore(
  value: number,
  bands: readonly ScoreBand[],
  env?: ConditionEnv
): ScoreBand | undefined {
  if (!Number.isFinite(value)) {
    return undefined
  }
  return bands.find(
    (band) =>
      (band.min === undefined || value >= band.min) &&
      (band.max === undefined || value <= band.max) &&
      (!band.when ||
        (env !== undefined && evaluateCondition(band.when, env) === true))
  )
}
